
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import FanArtCard from './FanArtCard';
import FanArtLightbox from './FanArtLightbox';

interface FanArtPost {
  id: string;
  title: string;
  content: string;
  image_url: string | null;
  created_at: string;
  author: {
    id: string;
    username: string;
    avatar_url: string | null;
  };
  like_count: number;
}

interface FanArtGridProps {
  sortBy: 'latest' | 'most_replies' | 'most_liked';
}

export default function FanArtGrid({ sortBy }: FanArtGridProps) {
  // Fixed generic syntax – removed escaped HTML entities
  const [posts, setPosts] = useState<FanArtPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState<FanArtPost | null>(null);

  useEffect(() => {
    fetchFanArtPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy]);

  const fetchFanArtPosts = async () => {
    setLoading(true);
    try {
      const { data: threadsData, error: threadsError } = await supabase
        .from('forum_threads')
        .select(`
          id,
          title,
          content,
          image_url,
          created_at,
          author:profiles!forum_threads_author_id_fkey (
            id,
            username,
            avatar_url
          )
        `)
        .eq('board', 'fanart');

      if (threadsError) throw threadsError;

      if (!threadsData || threadsData.length === 0) {
        setPosts([]);
        return;
      }

      const threadIds = threadsData.map((t: any) => t.id);

      // Get like counts
      const { data: likeCounts, error: likeError } = await supabase
        .from('forum_thread_likes')
        .select('thread_id')
        .in('thread_id', threadIds);
      if (likeError) throw likeError;

      const postsWithCounts = (threadsData as any[]).map((post) => ({
        ...post,
        like_count:
          likeCounts?.filter((l: any) => l.thread_id === post.id).length || 0,
      }));

      // Sort posts according to the selected criteria
      const sortedPosts = [...postsWithCounts].sort((a, b) => {
        if (sortBy === 'most_liked') {
          return b.like_count - a.like_count;
        }
        if (sortBy === 'most_replies') {
          // Placeholder: assuming a `reply_count` field exists.
          // If not, fallback to latest sorting.
          // @ts-expect-error reply_count is not in the type but may exist at runtime
          return (b.reply_count || 0) - (a.reply_count || 0);
        }
        // Default: latest
        return (
          new Date(b.created_at).getTime() -
          new Date(a.created_at).getTime()
        );
      });

      setPosts(sortedPosts);
    } catch (error) {
      console.error('Error fetching fan art posts:', error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLikeUpdate = () => {
    // Re‑fetch posts to reflect the new like count.
    fetchFanArtPosts();
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div
            key={i}
            className="bg-zinc-900/50 border border-red-900/20 animate-pulse"
          >
            <div className="aspect-square bg-zinc-800"></div>
            <div className="p-4 space-y-3">
              <div className="h-4 bg-zinc-800 rounded w-3/4"></div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-zinc-800"></div>
                <div className="h-3 bg-zinc-800 rounded w-24"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center">
          <i className="ri-image-line text-5xl text-red-900/30"></i>
        </div>
        <h3 className="text-xl font-['Cinzel'] text-zinc-400 mb-2">
          No Art Shared Yet
        </h3>
        <p className="text-zinc-500 font-['Crimson_Text'] max-w-md mx-auto">
          The gallery awaits its first masterpiece. Share your dark visions inspired by these haunting stories.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {posts.map((post) => (
          <FanArtCard
            key={post.id}
            post={post}
            onClick={() => setSelectedPost(post)}
          />
        ))}
      </div>

      {selectedPost && (
        <FanArtLightbox
          post={selectedPost}
          onClose={() => setSelectedPost(null)}
          onLikeUpdate={handleLikeUpdate}
        />
      )}
    </>
  );
}
